import {
  TableElementKey,
  TableSelectionAction,
  TableSelectionActionType,
} from './types';

type TableSelectionStateParams<T> = {
  selection?: TableElementKey<T>[];
  action?: TableSelectionActionType;
  selectedBy?: keyof T | null;
  hideFor?: TableElementKey<T>[];
};

export class TableSelectionState<T> {
  constructor(
    private readonly selection: TableElementKey<T>[],
    public readonly action: TableSelectionActionType,
    public readonly selectedBy?: keyof T | null,
    public readonly hideFor: TableElementKey<T>[] = []
  ) {
    this.selection = this.selection.filter(
      (key) => !this.hideFor.includes(key)
    );
  }

  static none<T>(): TableSelectionState<T> {
    return new TableSelectionState<T>([], 'none');
  }

  static from<T>(selection: TableSelectionAction<T>): TableSelectionState<T> {
    return new TableSelectionState(selection.data, selection.action);
  }

  public update(selection: TableSelectionAction<T>): TableSelectionState<T> {
    if (['all', 'none', 'input'].includes(selection.action)) {
      return TableSelectionState.from(selection);
    }

    return this.select(selection);
  }

  public select(selection: TableSelectionAction<T>): TableSelectionState<T> {
    const key = selection.data.pop()!;
    if (this.selection.includes(key)) {
      const filtered = this.selection.filter((element) => element !== key);
      return new TableSelectionState(filtered, selection.action);
    }

    return new TableSelectionState([...this.selection, key], selection.action);
  }

  public selected(keys: TableElementKey<T>): boolean {
    return this.selection.includes(keys);
  }

  public current(): TableElementKey<T>[] {
    return this.selection;
  }

  public empty(): boolean {
    return this.selection.length === 0;
  }

  public copyWith({
    selection,
    action,
    selectedBy,
    hideFor,
  }: TableSelectionStateParams<T>): TableSelectionState<T> {
    return new TableSelectionState(
      selection ?? this.selection,
      action ?? this.action,
      selectedBy ?? this.selectedBy,
      hideFor ?? this.hideFor
    );
  }
}
